import React, { useState, useEffect } from 'react';
import { DateEffetLotBar, useSelectionLot } from '../dateEffet/DateEffetLot';
import { DateEffetButton } from '../dateEffet/DateEffetDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Users, UserCheck, UserX, Clock, Search, TrendingUp, AlertTriangle, RefreshCw, BarChart3, PieChart, Bell, Mail, Eye, CheckCircle, Play, Receipt } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import PushNotificationManager from '../PushNotificationManager';

import { BACKEND_URL, formatDate, formatMonth, getExpirationDate } from './constants';
import AttestationDraftButton from './AttestationDraftButton';
import { SimpleBarChart, SimplePieChart } from './SimpleCharts';
import RappelsTab from './RappelsTab';
import AdhesionDetailModal from './AdhesionDetailModal';
import { getToken } from '../../services/tokenService';

const getStatusBadge = (status) => {
  const statusConfig = {
    active: { label: 'Active', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    completed: { label: 'Complete', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    pending: { label: 'En attente', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800 border-gray-200' },
    signed: { label: 'Signee', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    expired: { label: 'Expiree', className: 'bg-red-100 text-red-800 border-red-200' },
  };
  const config = statusConfig[status] || statusConfig.pending;
  return <Badge className={config.className}>{config.label}</Badge>;
};

export default function FTPEAdhesionsDashboard() {
  const lot = useSelectionLot();
  const { getAuthHeader, user } = useAuth();

  const [stats, setStats] = useState({
    total: 0, active: 0, pending: 0, expired: 0, signed: 0,
    byTerritory: {}, byMonth: [], expiringIn30Days: 0
  });
  const [adhesions, setAdhesions] = useState([]);
  const [expiringAdhesions, setExpiringAdhesions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [reminderConfig, setReminderConfig] = useState(null);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedAdhesion, setSelectedAdhesion] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [updatingConfig, setUpdatingConfig] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const calculateStats = (adhesionsList) => {
    const result = {
      total: adhesionsList.length, active: 0, pending: 0, expired: 0, signed: 0,
      byTerritory: {}, byMonth: [], expiringIn30Days: 0
    };
    const now = new Date();
    const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const monthsData = {};

    adhesionsList.forEach(a => {
      const status = a.status || 'pending';
      if (status === 'active' || status === 'completed' || status === 'signed') result.active++;
      else if (status === 'pending' || status === 'draft') result.pending++;
      else if (status === 'expired') result.expired++;

      const territoire = a.territoire || 'Autre';
      result.byTerritory[territoire] = (result.byTerritory[territoire] || 0) + 1;

      const createdAt = new Date(a.created_at || a.date_creation);
      if (!isNaN(createdAt)) {
        const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        monthsData[monthKey] = (monthsData[monthKey] || 0) + 1;
      }

      if (a.expiration_date) {
        const expDate = new Date(a.expiration_date);
        if (expDate >= now && expDate <= in30Days) result.expiringIn30Days++;
      }
    });

    result.byMonth = Object.entries(monthsData)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: formatMonth(month), count }));

    return result;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [_statsRes, adhesionsRes, expiringRes, reminderRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ftpe-adhesion/admin/statistics`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/ftpe-adhesion/admin/list`, { headers }).catch(() => ({ data: { adhesions: [] } })),
        axios.get(`${BACKEND_URL}/api/ftpe-adhesion/admin/expiring-adhesions?days=30`, { headers }).catch(() => ({ data: { adhesions: [] } })),
        axios.get(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/config`, { headers }).catch(() => ({ data: { config: null } }))
      ]);

      const allAdhesions = adhesionsRes.data.adhesions || [];
      setStats(calculateStats(allAdhesions));
      setAdhesions(allAdhesions);
      setExpiringAdhesions(expiringRes.data.adhesions || []);
      setReminderConfig(reminderRes.data.config);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/trigger`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Rappels envoyes');
      fetchData();
    } catch { toast.error("Erreur lors de l'envoi des rappels"); }
    finally { setTriggeringReminders(false); }
  };

  const handleSendIndividualReminder = async (adhesionId, email) => {
    setSendingReminderId(adhesionId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/send-individual/${adhesionId}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || `Rappel envoye a ${email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du rappel");
    } finally { setSendingReminderId(null); }
  };

  const handleToggleReminders = async () => {
    setUpdatingConfig(true);
    try {
      const newEnabled = !reminderConfig?.enabled;
      await axios.post(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/config`, { enabled: newEnabled }, { headers: getAuthHeader() });
      setReminderConfig(prev => ({ ...prev, enabled: newEnabled }));
      toast.success(newEnabled ? 'Rappels automatiques actives' : 'Rappels automatiques desactives');
    } catch { toast.error('Erreur lors de la mise a jour de la configuration'); }
    finally { setUpdatingConfig(false); }
  };

  const handleUpdateIntervals = async (intervals) => {
    setUpdatingConfig(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/config`, { intervals_days: intervals }, { headers: getAuthHeader() });
      setReminderConfig(prev => ({ ...prev, intervals_days: intervals }));
      toast.success('Intervalles de rappel mis a jour');
    } catch { toast.error('Erreur lors de la mise a jour'); }
    finally { setUpdatingConfig(false); }
  };

  const fetchReminderHistory = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ftpe-adhesion/admin/reminders/history`, { headers: getAuthHeader() });
      setReminderHistory(res.data.adhesions_with_reminders || []);
    } catch (error) { console.error('Erreur chargement historique:', error); }
  };

  const handleActivateAdhesion = async (adhesionId) => {
    if (!adhesionId || adhesionId === 'undefined' || adhesionId === 'null') {
      toast.error("ID de l'adhesion manquant ou invalide");
      return;
    }
    if (!window.confirm("Etes-vous sur de vouloir activer cette adhesion ? Cela lui donnera acces aux services pour 1 an.")) return;

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ftpe-adhesion/admin/activate/${adhesionId}`, {}, { headers: getAuthHeader() });
      if (response.data.success) {
        toast.success(`Adhesion activee pour ${response.data.raison_sociale || "l'entreprise"}`);
        fetchData();
      } else {
        toast.error(response.data.message || "Erreur lors de l'activation");
      }
    } catch (error) {
      let errorMsg = "Erreur lors de l'activation";
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        if (status === 404) errorMsg = `Adhesion non trouvee (ID: ${adhesionId}).`;
        else if (status === 403) errorMsg = 'Acces refuse.';
        else if (status === 401) errorMsg = 'Session expiree.';
        else if (detail) errorMsg = detail;
        else errorMsg = `Erreur ${status}: ${error.response.statusText}`;
      }
      toast.error(errorMsg);
    }
  };

  const filteredAdhesions = adhesions.filter(a => {
    const matchSearch = !searchQuery ||
      (a.raison_sociale || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.territoire || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Chart data
  const territoryChartData = Object.entries(stats.byTerritory || {}).map(([name, value], idx) => ({
    label: name, value,
    color: ['bg-blue-500', 'bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500'][idx % 5],
    colorHex: ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'][idx % 5]
  }));

  const statusChartData = [
    { label: 'Actives', value: stats.active, color: 'bg-emerald-500', colorHex: '#10b981' },
    { label: 'En attente', value: stats.pending, color: 'bg-blue-500', colorHex: '#f59e0b' },
    { label: 'Signees', value: stats.signed, color: 'bg-blue-500', colorHex: '#3b82f6' },
    { label: 'Expirees', value: stats.expired, color: 'bg-red-500', colorHex: '#ef4444' },
  ].filter(d => d.value > 0);

  const monthlyChartData = (stats.byMonth || []).map(m => ({
    label: m.month, value: m.count, color: 'bg-blue-500'
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tete */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de bord FTPE</h2>
          <p className="text-gray-500">Suivi des adhesions a la Federation</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          {reminderConfig?.enabled && (
            <Button size="sm" onClick={handleTriggerReminders} disabled={triggeringReminders} className="bg-blue-500 hover:bg-blue-600">
              {triggeringReminders ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Envoyer rappels
            </Button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); if (tab === 'rappels') fetchReminderHistory(); }}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Statistiques
          </TabsTrigger>
          <TabsTrigger value="rappels" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Rappels Email
            {expiringAdhesions.length > 0 && (
              <Badge className="bg-orange-600 text-white text-xs ml-1 font-semibold">{expiringAdhesions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications Push
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="mt-4">
          <PushNotificationManager />
        </TabsContent>

        <TabsContent value="rappels" className="mt-4">
          <RappelsTab
            expiringAdhesions={expiringAdhesions}
            reminderConfig={reminderConfig}
            reminderHistory={reminderHistory}
            triggeringReminders={triggeringReminders}
            sendingReminderId={sendingReminderId}
            updatingConfig={updatingConfig}
            onTriggerReminders={handleTriggerReminders}
            onSendIndividualReminder={handleSendIndividualReminder}
            onToggleReminders={handleToggleReminders}
            onUpdateIntervals={handleUpdateIntervals}
          />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4 space-y-6">
          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl"><Users className="w-6 h-6 text-blue-600" /></div>
                  <div><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-bold text-gray-900">{stats.total}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 rounded-xl"><UserCheck className="w-6 h-6 text-emerald-600" /></div>
                  <div><p className="text-sm text-gray-500">Actives</p><p className="text-2xl font-bold text-emerald-600">{stats.active}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl"><Clock className="w-6 h-6 text-blue-600" /></div>
                  <div><p className="text-sm text-gray-500">En attente</p><p className="text-2xl font-bold text-blue-600">{stats.pending + stats.signed}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-xl"><UserX className="w-6 h-6 text-red-600" /></div>
                  <div><p className="text-sm text-gray-500">Expirees</p><p className="text-2xl font-bold text-red-600">{stats.expired}</p></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerte expirations */}
          {expiringAdhesions.length > 0 && (
            <Card className="border-blue-300 bg-blue-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800">{expiringAdhesions.length} adhesion(s) expire(nt) dans les 30 prochains jours</p>
                    <p className="text-sm text-blue-700">Un rappel automatique sera envoye aux adherents concernes.</p>
                  </div>
                  <Button variant="outline" size="sm" className="border-blue-400 text-blue-700 hover:bg-blue-100">
                    <Eye className="w-4 h-4 mr-2" />
                    Voir details
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Graphiques */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-blue-500" />
                  Repartition par statut
                </CardTitle>
              </CardHeader>
              <CardContent><SimplePieChart data={statusChartData} title="" /></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                  Par territoire
                </CardTitle>
              </CardHeader>
              <CardContent><SimpleBarChart data={territoryChartData} title="" /></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Tendance (6 derniers mois)
                </CardTitle>
              </CardHeader>
              <CardContent><SimpleBarChart data={monthlyChartData} title="" /></CardContent>
            </Card>
          </div>

          {/* Table des adhesions */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="text-lg">Liste des adhesions</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 w-64" />
                  </div>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actives</option>
                    <option value="pending">En attente</option>
                    <option value="signed">Signees</option>
                    <option value="expired">Expirees</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DateEffetLotBar selection={lot} onDone={() => fetchData()} />
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="w-8 pb-3"><input type="checkbox" className="h-4 w-4 accent-[#0a3d62]" checked={filteredAdhesions.slice(0, 20).length > 0 && filteredAdhesions.slice(0, 20).every((a) => lot.coche('ftpe', a.id))} onChange={(e) => lot.toutes('ftpe', filteredAdhesions.slice(0, 20).map((a) => a.id), e.target.checked)} data-testid="ftpe-lot-tout" /></th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Entreprise</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Territoire</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Statut</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Paiement</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Date adhesion</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Expiration</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Attestation</th>
                      <th className="pb-3 font-medium text-gray-500 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdhesions.length === 0 ? (
                      <tr><td colSpan={9} className="py-8 text-center text-gray-500">Aucune adhesion trouvee</td></tr>
                    ) : (
                      filteredAdhesions.slice(0, 20).map((adhesion, idx) => (
                        <tr key={adhesion.id} className="border-b hover:bg-gray-50">
                          <td className="py-3"><input type="checkbox" className="h-4 w-4 accent-[#0a3d62]" checked={lot.coche('ftpe', adhesion.id)} onChange={() => lot.toggle('ftpe', adhesion.id)} data-testid={`ftpe-lot-check-${idx}`} /></td>
                          <td className="py-3">
                            <div>
                              <p className="font-medium text-gray-900">{adhesion.raison_sociale || 'N/A'}</p>
                              <p className="text-xs text-gray-500">{adhesion.email}</p>
                            </div>
                          </td>
                          <td className="py-3"><Badge variant="outline" className="text-xs">{adhesion.territoire || 'N/A'}</Badge></td>
                          <td className="py-3">{getStatusBadge(adhesion.status)}</td>
                          <td className="py-3">
                            {(adhesion.payment_method === 'stripe' || adhesion.payment_method === 'CB' || adhesion.payment_method === 'card') && (
                              <Badge className="bg-purple-700 text-white border-purple-900 font-semibold">CB</Badge>
                            )}
                            {(adhesion.payment_method === 'virement' || adhesion.payment_method === 'Virement' || adhesion.payment_method === 'bank_transfer') && (
                              <Badge className="bg-blue-700 text-white border-blue-900 font-semibold">Virement</Badge>
                            )}
                            {(adhesion.payment_method === 'manual_activation' || adhesion.payment_method === 'manual' || adhesion.payment_method === 'Manuel') && (
                              <Badge className="bg-green-700 text-white border-green-900 font-semibold">Manuel</Badge>
                            )}
                            {!adhesion.payment_method && adhesion.payment_status === 'paid' && (
                              <Badge className="bg-gray-100 text-gray-800 border-gray-200 font-semibold">Paye</Badge>
                            )}
                            {!adhesion.payment_method && adhesion.payment_status !== 'paid' && (
                              <Badge variant="outline" className="text-gray-500 border-gray-200">-</Badge>
                            )}
                          </td>
                          <td className="py-3 text-sm text-gray-600">{formatDate(adhesion.created_at || adhesion.date_creation)}</td>
                          <td className="py-3 text-sm text-gray-600">{getExpirationDate(adhesion)}</td>
                          <td className="py-3" data-testid={`attestation-cell-${adhesion.id}`}>
                            <AttestationDraftButton adhesion={adhesion} />
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedAdhesion(adhesion); setShowDetailModal(true); }} title="Voir les details">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {user?.role === 'super_admin' && (
                                <DateEffetButton produit="ftpe" ident={adhesion.id} compact onDone={() => fetchData()} testId={`ftpe-dates-btn-${adhesion.id}`} />
                              )}
                              {!['active', 'completed'].includes(adhesion.status) && adhesion.status !== 'expired' && (
                                <Button variant="ghost" size="sm" onClick={() => handleActivateAdhesion(adhesion.id)} title="Activer l'adhesion" className="text-green-600 hover:text-green-700 hover:bg-green-50">
                                  <Play className="w-4 h-4" />
                                </Button>
                              )}
                              {['active', 'completed'].includes(adhesion.status) && (
                                <span className="text-emerald-600 px-2" title="Adhesion active"><CheckCircle className="w-4 h-4" /></span>
                              )}
                              {adhesion.status === 'active' && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => {
                                    const token = getToken();
                                    const url = `${BACKEND_URL}/api/ftpe-adhesion/recu-cotisation/${adhesion.id}/download?token=${encodeURIComponent(token)}`;
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `RECU_COTISATION_FTPE_${adhesion.raison_sociale || adhesion.id}.pdf`;
                                    a.target = '_blank';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  }}
                                  title="Telecharger le recu de cotisation acquitte"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  data-testid={`recu-cotisation-${adhesion.id}`}
                                >
                                  <Receipt className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredAdhesions.length > 20 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  Affichage des 20 premieres adhesions sur {filteredAdhesions.length}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal details */}
      <AdhesionDetailModal
        adhesion={selectedAdhesion}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        onUpdated={fetchData}
      />
    </div>
  );
}
